export interface FAQItem {
  categoria: string;
  pergunta: string;
  resposta: string;
}

export interface TopicoManual {
  titulo: string;
  descricao: string;
}

export interface SecaoManual {
  id: string;
  titulo: string;
  emoji: string;
  diagrama?: string; // chave do diagrama SVG (ver diagramas.tsx)
  topicos: TopicoManual[];
}

export interface HelpContent {
  titulo: string;
  faq: FAQItem[];
  manual: SecaoManual[];
}

// ─── Central de Ajuda — ADMIN (equipe interna do sistema) ─────────────────────

export const adminHelp: HelpContent = {
  titulo: "Central de Ajuda — Administração",
  faq: [
    {
      categoria: "Clientes",
      pergunta: "Como cadastro um cliente?",
      resposta:
        "Acesse Dashboard → Clientes → \"Novo Cliente\". Preencha os dados gerais (razão social, CNPJ, segmento), endereço e o acesso ao portal. O código interno é gerado automaticamente (CLI-0001).",
    },
    {
      categoria: "Clientes",
      pergunta: "Como configuro o prazo de vencimento de um cliente?",
      resposta:
        "No cadastro do cliente, aba \"Dados Gerais\", informe o campo \"Prazo de Vencimento (dias)\" (padrão 30). Esse valor é usado na geração automática da fatura quando uma proposta é aprovada.",
    },
    {
      categoria: "Propostas",
      pergunta: "Como criar e enviar uma proposta?",
      resposta:
        "Dashboard → Propostas → \"Nova Proposta\". Selecione o cliente, preencha o valor (ou use um modelo), salve como rascunho e envie ao cliente. O cliente aprova/rejeita pelo Portal.",
    },
    {
      categoria: "Propostas",
      pergunta: "O que acontece ao aprovar uma proposta?",
      resposta:
        "Ao aprovar, o sistema gera automaticamente uma fatura em rascunho, usando o prazo de vencimento configurado no cliente. O valor vem do valorGlobal da proposta (ou da soma dos itens).",
    },
    {
      categoria: "Faturas",
      pergunta: "Como gero uma fatura?",
      resposta:
        "Há três formas: (1) automaticamente ao aprovar uma proposta; (2) a partir de contratos (faturamento recorrente/mensal); (3) manualmente em Faturas → \"Nova Fatura\".",
    },
    {
      categoria: "Faturas",
      pergunta: "Como controlo o status de uma fatura?",
      resposta:
        "Em Faturas, cada fatura tem um status (Rascunho, Emitida, Enviada, Paga, Cancelada, Vencida) com transições permitidas. Use as ações rápidas ou o menu de status.",
    },
    {
      categoria: "Atendimentos",
      pergunta: "Como funciona a catraca de aprovação de tickets?",
      resposta:
        "Tickets com horas/valor estimados nascem \"Aguardando Aprovação\". O cliente aprova no Portal; você também pode \"Aprovar Demanda\" no detalhe do ticket (forçar aprovação).",
    },
    {
      categoria: "Atendimentos",
      pergunta: "Como registro apontamentos de horas?",
      resposta:
        "Abra o ticket e clique em \"Novo Apontamento\". Informe data, hora início/fim, tipo de hora (HTC/HTE/PROJETO) e descrição. O valor é calculado automaticamente.",
    },
    {
      categoria: "Atendimentos",
      pergunta: "Como faturar as horas de um cliente?",
      resposta:
        "No detalhe do ticket, use \"Faturar em Lote\". O sistema consolida os apontamentos não faturados do cliente em uma fatura (rascunho).",
    },
    {
      categoria: "Documentos",
      pergunta: "Como vincular documentos às faturas (Centralização Financeira)?",
      resposta:
        "Em Faturas, clique no ícone de documento da fatura. Vincule notas fiscais/boletos já enviados (ou desvincule) para manter a auditoria em um só lugar.",
    },
    {
      categoria: "Modelos",
      pergunta: "Como crio modelos de proposta e contrato?",
      resposta:
        "Dashboard → \"Modelos Proposta\" ou \"Modelos Contrato\". Crie modelos reutilizáveis com conteúdo e valor padrão, usados na criação de propostas/contratos.",
    },
    {
      categoria: "Usuários",
      pergunta: "Como gerencio usuários e permissões?",
      resposta:
        "Dashboard → Usuários. Crie usuários com perfil (admin, técnico). Técnicos podem ser vinculados a clientes específicos (clientes gerenciados).",
    },
    {
      categoria: "Portal",
      pergunta: "Como publico propostas, faturas ou contratos no Portal do Cliente?",
      resposta:
        "Na listagem do documento (proposta, fatura ou contrato), clique no ícone de publicação. O item fica visível para o cliente no Portal até você despublicar.",
    },
    {
      categoria: "Contratos",
      pergunta: "Como funciona o faturamento mensal de contratos?",
      resposta:
        "Use \"Gerar Faturas do Mês\" no módulo de Faturas (ou a rotina automática no dia 01 de cada mês). O sistema cria uma fatura rascunho para cada contrato ativo, ignorando os que já têm fatura no mês.",
    },
  ],
  manual: [
    {
      id: "clientes",
      titulo: "Clientes",
      emoji: "👥",
      topicos: [
        { titulo: "Cadastro", descricao: "Dados gerais, endereço e acesso ao portal. Código interno gerado automaticamente (CLI-0001)." },
        { titulo: "Prazo de vencimento", descricao: "Configure o prazo (dias) usado na fatura gerada a partir de propostas." },
        { titulo: "Unidades", descricao: "Cadastre múltiplas unidades por cliente (a unidade \"Matriz\" é criada automaticamente)." },
        { titulo: "Usuários do cliente", descricao: "Crie acessos individuais ao portal para cada usuário do cliente." },
      ],
    },
    {
      id: "propostas",
      titulo: "Propostas",
      emoji: "📄",
      diagrama: "proposta-fatura",
      topicos: [
        { titulo: "Criação", descricao: "Use modelos ou preencha manualmente; defina valor (valorGlobal) e validade." },
        { titulo: "Fluxo", descricao: "Rascunho → Enviada → Aprovada/Rejeitada (com máquina de estados)." },
        { titulo: "Geração de fatura", descricao: "Ao aprovar, a fatura é criada automaticamente com o prazo do cliente." },
        { titulo: "PDF", descricao: "Gere o PDF da proposta a qualquer momento." },
      ],
    },
    {
      id: "contratos",
      titulo: "Contratos",
      emoji: "📑",
      topicos: [
        { titulo: "Criação", descricao: "Contratos recorrentes com itens e valor mensal." },
        { titulo: "Faturamento mensal", descricao: "Use \"Gerar Faturas do Mês\" para criar as faturas dos contratos ativos." },
        { titulo: "MRR", descricao: "Acompanhe a receita recorrente mensal no módulo de contratos." },
      ],
    },
    {
      id: "faturas",
      titulo: "Faturas",
      emoji: "💰",
      diagrama: "faturamento",
      topicos: [
        { titulo: "Criação", descricao: "Manual, a partir de propostas (automático) ou de contratos (mensal)." },
        { titulo: "Status", descricao: "Rascunho → Emitida → Enviada → Paga; com cancelamento e vencimento." },
        { titulo: "Centralização Financeira", descricao: "Vincule documentos (NFs, boletos) à fatura." },
      ],
    },
    {
      id: "atendimentos",
      titulo: "Atendimentos (Tickets)",
      emoji: "🎧",
      diagrama: "ticket-vida",
      topicos: [
        { titulo: "Tickets", descricao: "Crie chamados por cliente; tickets com estimativa entram na catraca de aprovação." },
        { titulo: "Apontamentos", descricao: "Registre horas trabalhadas (HTC/HTE/PROJETO) por chamado." },
        { titulo: "Faturamento", descricao: "Fature em lote os apontamentos não faturados de um cliente." },
        { titulo: "Relatório", descricao: "Gere o relatório técnico em PDF de cada ticket." },
      ],
    },
    {
      id: "documentos",
      titulo: "Documentos (PMOC)",
      emoji: "🗂️",
      topicos: [
        { titulo: "Upload", descricao: "Envie documentos e categorize por tipo." },
        { titulo: "Vínculo à fatura", descricao: "Associe documentos a faturas para centralização financeira." },
      ],
    },
    {
      id: "catalogo",
      titulo: "Produtos/Serviços e Modelos",
      emoji: "📦",
      topicos: [
        { titulo: "Produtos/Serviços", descricao: "Cadastre itens com valor para reutilizar em propostas e faturas." },
        { titulo: "Modelos", descricao: "Modelos de proposta e contrato reutilizáveis com conteúdo e valor padrão." },
      ],
    },
    {
      id: "usuarios",
      titulo: "Usuários e Configurações",
      emoji: "⚙️",
      topicos: [
        { titulo: "Usuários", descricao: "Administradores e técnicos; vínculo de técnicos a clientes gerenciados." },
        { titulo: "Configurações", descricao: "Identidade visual (white-label), cores, logotipo e tema." },
      ],
    },
  ],
};

// ─── Central de Ajuda — CLIENTE (Portal do Cliente) ───────────────────────────

export const clientHelp: HelpContent = {
  titulo: "Central de Ajuda — Portal do Cliente",
  faq: [
    {
      categoria: "Atendimento",
      pergunta: "Como aprovo uma demanda com estimativa de horas/valor?",
      resposta:
        "Quando um chamado possui estimativa, ele fica em \"Aguardando Aprovação\". Abra o chamado no Portal e clique em \"Aprovar Demanda\". Somente após a sua aprovação a equipe inicia a execução.",
    },
    {
      categoria: "Atendimento",
      pergunta: "Como abro um novo chamado (ticket)?",
      resposta:
        "No Portal, acesse \"Tickets\" e clique em \"Novo Ticket\". Preencha título, descrição e prioridade. Se souber o esforço estimado, informe horas/valor para que a demanda entre no fluxo de aprovação.",
    },
    {
      categoria: "Atendimento",
      pergunta: "Como acompanho o andamento de um chamado?",
      resposta:
        "Abra o chamado e veja a \"Timeline de Interações\" com o histórico da equipe, respostas e anexos. O status indica a etapa (Aberto, Em Andamento, Aguardando Cliente, Resolvido, Fechado).",
    },
    {
      categoria: "Faturas",
      pergunta: "Onde encontro minhas faturas e como baixo o PDF?",
      resposta:
        "No Portal, acesse \"Faturas\". Você vê a lista com status e pode abrir cada fatura para baixar o PDF.",
    },
    {
      categoria: "Faturas",
      pergunta: "Como funciona o prazo de vencimento das faturas?",
      resposta:
        "O vencimento é definido por cliente. Ao aprovar uma proposta, a fatura é gerada automaticamente com o prazo configurado para a sua empresa.",
    },
    {
      categoria: "Propostas",
      pergunta: "O que é uma proposta e como aprová-la?",
      resposta:
        "A proposta é o documento comercial com escopo e valores. Em \"Propostas\", visualize o conteúdo e aprove ou rejeite. Ao aprovar, a fatura é gerada automaticamente.",
    },
    {
      categoria: "Documentos",
      pergunta: "Como envio documentos (PMOC, notas fiscais)?",
      resposta:
        "Na seção \"Documentos\", selecione o tipo e faça o upload. Os documentos podem ser vinculados às faturas.",
    },
    {
      categoria: "Acesso",
      pergunta: "Esqueci minha senha. O que faço?",
      resposta:
        "Entre em contato com o responsável pelo seu acesso (administrador do sistema). Ele pode redefinir sua senha de acesso ao portal.",
    },
    {
      categoria: "Atendimento",
      pergunta: "O que são apontamentos de horas?",
      resposta:
        "São os registros do tempo trabalhado pela equipe em cada chamado. Eles alimentam o faturamento: as horas viram itens da fatura.",
    },
    {
      categoria: "Faturas",
      pergunta: "O que significa o status da minha fatura?",
      resposta:
        "Rascunho (não enviada), Emitida (gerada), Enviada (encaminhada a você), Paga (quitada) e Vencida (passou do vencimento sem pagamento).",
    },
    {
      categoria: "Documentos",
      pergunta: "Como faço o download de documentos e anexos?",
      resposta:
        "Em \"Documentos\" ou na timeline do chamado, clique no item desejado e use o botão de download. Os anexos enviados pela equipe também ficam disponíveis na timeline.",
    },
    {
      categoria: "Propostas",
      pergunta: "Qual a diferença entre aprovar uma proposta e aprovar um chamado?",
      resposta:
        "Aprovar uma proposta gera automaticamente uma fatura (comercial). Aprovar um chamado (demanda) apenas libera a execução do serviço. São fluxos independentes.",
    },
  ],
  manual: [
    {
      id: "portal",
      titulo: "Portal do Cliente",
      emoji: "🏠",
      topicos: [
        { titulo: "Dashboard", descricao: "Visão geral da sua conta: últimos chamados, faturas e documentos." },
        { titulo: "Navegação", descricao: "Use o menu lateral para acessar Tickets, Faturas, Documentos, Propostas e Contratos." },
        { titulo: "Ajuda", descricao: "A qualquer momento, clique no ícone \"?\" no topo para abrir esta central." },
      ],
    },
    {
      id: "tickets",
      titulo: "Tickets / Atendimento",
      emoji: "🎧",
      diagrama: "aprovacao",
      topicos: [
        { titulo: "Abrir chamado", descricao: "Descreva o problema com clareza e defina a prioridade." },
        { titulo: "Aprovar demanda", descricao: "Chamados com estimativa ficam \"Aguardando Aprovação\" até você autorizar." },
        { titulo: "Responder", descricao: "Quando o status é \"Aguardando Cliente\", responda pela timeline para dar continuidade." },
      ],
    },
    {
      id: "faturas",
      titulo: "Faturas",
      emoji: "💰",
      topicos: [
        { titulo: "Visualizar", descricao: "Consulte suas faturas e acompanhe os status e vencimentos." },
        { titulo: "Baixar PDF", descricao: "Abra a fatura e baixe o PDF para pagamento e arquivo." },
      ],
    },
    {
      id: "documentos",
      titulo: "Documentos",
      emoji: "🗂️",
      topicos: [
        { titulo: "Enviar", descricao: "Faça upload de documentos (PMOC, notas fiscais, boletos) por tipo." },
        { titulo: "Visualizar", descricao: "Acesse os documentos disponibilizados para a sua empresa." },
      ],
    },
    {
      id: "propostas-contratos",
      titulo: "Propostas e Contratos",
      emoji: "📄",
      diagrama: "proposta-fatura",
      topicos: [
        { titulo: "Propostas", descricao: "Visualize e aprove/rejeite propostas comerciais." },
        { titulo: "Contratos", descricao: "Consulte seus contratos vigentes." },
      ],
    },
  ],
};
